/**
 * PWA Store - Manages Progressive Web App installation state
 * Handles beforeinstallprompt event and standalone mode detection
 */
import { create } from 'zustand';

// Type for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAState {
  // State
  installPrompt: BeforeInstallPromptEvent | null;
  isInstallable: boolean;
  isStandalone: boolean;
  isInstalling: boolean;

  // Actions
  setInstallPrompt: (event: BeforeInstallPromptEvent | null) => void;
  setIsInstallable: (installable: boolean) => void;
  setIsStandalone: (standalone: boolean) => void;
  promptInstall: () => Promise<boolean>;
  checkStandaloneMode: () => void;
  initializePWA: () => void;
}

export const usePWAStore = create<PWAState>((set, get) => ({
  // Initial state
  installPrompt: null,
  isInstallable: false,
  isStandalone: false,
  isInstalling: false,

  // Set the install prompt event
  setInstallPrompt: (event) => {
    set({ installPrompt: event, isInstallable: event !== null });
  },

  // Set installable state
  setIsInstallable: (installable) => {
    set({ isInstallable: installable });
  },

  // Set standalone state
  setIsStandalone: (standalone) => {
    set({ isStandalone: standalone });
  },

  // Trigger the install prompt
  promptInstall: async () => {
    const { installPrompt } = get();

    if (!installPrompt) {
      return false;
    }

    set({ isInstalling: true });

    try {
      // Show the install prompt
      await installPrompt.prompt();

      // Wait for the user's choice
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        // Clear the prompt as it can only be used once
        set({ installPrompt: null, isInstallable: false, isInstalling: false });
        return true;
      } else {
        set({ isInstalling: false });
        return false;
      }
    } catch (error) {
      console.error('Error during PWA installation:', error);
      set({ isInstalling: false });
      return false;
    }
  },

  // Check if running in standalone mode (installed PWA)
  checkStandaloneMode: () => {
    // Check various methods to detect standalone/PWA mode
    const isStandalone =
      // Standard check for display-mode
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari specific
      (window.navigator as any).standalone === true ||
      // Check if running in fullscreen mode
      window.matchMedia('(display-mode: fullscreen)').matches ||
      // Check document referrer (empty when opened from home screen)
      document.referrer.includes('android-app://');

    set({ isStandalone });
  },

  // Initialize PWA event listeners
  initializePWA: () => {
    const { checkStandaloneMode, setInstallPrompt, setIsInstallable } = get();

    // Check initial standalone mode
    checkStandaloneMode();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', (e) => {
      set({ isStandalone: e.matches });
    });

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful app installation
    window.addEventListener('appinstalled', () => {
      setInstallPrompt(null);
      setIsInstallable(false);
      set({ isStandalone: true });
    });

    // Return cleanup function
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      mediaQuery.removeEventListener('change', () => {});
    };
  },
}));
